import { jsx as _jsx } from "react/jsx-runtime";
import { freddyTheme, getSxStyles, mergeStyles } from '@coolblue-development/freddy';
import { Box } from '@coolblue-development/freddy';
import { version } from 'react';
import { getImageUrl } from '../imageUrl.js';
export const BaseImage = ({ alt, background, className, width, height, fileCollection, imageId, loading, objectFit, objectPosition, onLoad, fetchPriority, sizes = '', fullWidth = false, fullHeight = false, isDisabled = false, environment, subsidiary, sx, ...rest }) => {
    var _a;
    const sources = getImageUrl({
        background,
        width,
        height,
        fileCollection,
        imageId,
        objectFit,
        fullWidth,
        environment,
        subsidiary,
    });
    const srcUrl = (_a = sources.find(({ requestedPixelRatio }) => requestedPixelRatio === 1)) === null || _a === void 0 ? void 0 : _a.url;
    const srcSet = sources.map(({ url, width }) => `${url} ${width}w`).join(', ');
    return (_jsx(Box, { alt: alt, 
        // It's intended to keep `loading` before `src` because React updates
        // props in order which causes Safari/Firefox to not lazy load properly.
        // See https://github.com/facebook/react/issues/25883
        loading: loading, className: className, component: "img", onLoad: onLoad, sx: mergeStyles([
            isDisabled && { filter: 'grayscale(1)' },
            getObjectFitStyles(objectFit),
            getObjectPositionStyles(objectPosition),
            getBackgroundStyles(background),
            getSxStyles(freddyTheme, sx),
        ]), width: getWidth(width, fullWidth), height: getHeight(height, fullHeight, objectFit), sizes: !!sizes ? sizes : getDefaultSizes(width, fullWidth), srcSet: srcSet, 
        // It's intended to keep `src` the last attribute because React updates
        // attributes in order. If we have `src` as the first one, Safari will
        // immediately start to fetch `src`, before `sizes` and `srcSet` are even
        // updated by React. That causes multiple unnecessary requests if `srcSet`
        // and `sizes` are defined.
        // This bug cannot be reproduced in Chrome or Firefox.
        src: srcUrl, ...getFetchPriorityAttribute(fetchPriority), ...rest }));
};
const getDefaultSizes = (width, fullWidth) => {
    if (fullWidth) {
        // Ideal for images that cover the entire Freddy Container width,
        // calculated with help of https://github.com/ausi/respimagelint
        return `(min-width: 1400px) 1280px, 93.7vw`;
    }
    return `${width}px`;
};
const getObjectFitStyles = (objectFit) => {
    switch (objectFit) {
        case 'scale':
            return { objectFit: 'none' };
        case 'cover':
            return { objectFit: 'cover' };
        case 'contain':
        default:
            return { objectFit: 'contain' };
    }
};
const getObjectPositionStyles = (objectPosition) => {
    return objectPosition ? { objectPosition } : {};
};
const getBackgroundStyles = (background) => {
    if (background === 'solid') {
        return { backgroundColor: freddyTheme.color.background.base };
    }
    return {};
};
const getWidth = (width, fullWidth) => {
    if (fullWidth) {
        return '100%';
    }
    return width;
};
const getHeight = (height, fullHeight, objectFit) => {
    if (fullHeight) {
        return '100%';
    }
    if (objectFit === 'scale') {
        return undefined;
    }
    return height;
};
const getFetchPriorityAttribute = (fetchPriority) => {
    if (!fetchPriority) {
        return {};
    }
    // React 18.3.x-canary which Nextjs 14 uses has `fetchPriority` while the non-canary React 18.3.x which vitest uses has `fetchpriority`
    // If the casing is not correct, it will give a "Warning: Invalid DOM property" warning.
    const [major] = version.split('.', 1).map(str => parseInt(str, 10));
    const isCanary = version.includes('canary');
    if (major > 18 || (major === 18 && isCanary)) {
        return { fetchPriority };
    }
    return { fetchpriority: fetchPriority };
};
//# sourceMappingURL=BaseImage.js.map